// InputAddress.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid2";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { Divider, Radio, RadioGroup, CircularProgress } from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import axios from "../../api/axios";
import { toSnakeCase } from "../utils/general_utils";

const parseBool = (v) => (v === "true" ? true : v === "false" ? false : null);

function getModeFromEstadoStr(hasEstado, estadoStr) {
  if (!hasEstado) return "none";
  const e = (estadoStr ?? "").trim();
  if (e === "Pendiente") return "pending";
  if (e === "No Verificado") return "rejected";
  if (e === "Verificado") return "verified";
  return "other";
}

export default function InputAddress({
  rbd,
  direccionActual,
  creadoPor = "",
  onSaved,
  onModeChange,
  margin = 0,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [direccion_ok, setDireccionOk] = useState(null);
  const [internet_ok, setInternetOk] = useState(null);
  const [direccion_texto, setDireccionTexto] = useState("");

  const [estadoStr, setEstadoStr] = useState(null);
  const [hasEstado, setHasEstado] = useState(false);

  const [errorDireccionTexto, setErrorDireccionTexto] = useState(false);

  const mode = useMemo(() => getModeFromEstadoStr(hasEstado, estadoStr), [hasEstado, estadoStr]);

  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  const isPending = mode === "pending";
  const isRejected = mode === "rejected";
  const disableInputs = loading || saving || isPending;

  const styles = useMemo(() => {
    if (mode === "pending") return { boxBg: "#FFFFFF", border: "2px solid #1976d2" };
    if (mode === "rejected") return { boxBg: "#F4D0C6", border: "0px solid transparent" };
    if (mode === "verified") return { boxBg: "#E4E4E4", border: "1px solid rgba(0,0,0,0.08)" };
    if (mode === "other") return { boxBg: "#E4E4E4", border: "1px solid rgba(0,0,0,0.08)" };
    return { boxBg: "#BBDEFB", border: "1px solid rgba(0,0,0,0.08)" };
  }, [mode]);

  const direccionParaInternet = useMemo(() => {
    if (direccion_ok === false) return direccion_texto.trim() || (direccionActual ?? "");
    return direccionActual ?? "";
  }, [direccion_ok, direccion_texto, direccionActual]);

  const rejectedAddress = useMemo(() => {
    return (direccion_texto ?? "").trim() || (direccionActual ?? "");
  }, [direccion_texto, direccionActual]);

  const reloadResumen = useCallback(async () => {
    if (!rbd) return;

    const resp = await axios.getResumenIntentosRBD(rbd);
    const data = resp?.data ?? resp ?? {};


    const intento = data?.intento ?? {};
    const estado = data?.estado ?? null;

    const nextHasEstado = !!estado;
    const nextEstadoStr = estado?.estado ?? null;

    setHasEstado(nextHasEstado);
    setEstadoStr(nextEstadoStr);

    const nextMode = getModeFromEstadoStr(nextHasEstado, nextEstadoStr);
    onModeChange?.(nextMode);

    if (nextMode === "other" || nextMode === "rejected" || nextMode === "none" || nextMode === "verified") {
      setDireccionOk(null);
      setInternetOk(null);
      if(nextMode === "rejected"){
        setDireccionTexto(intento.direccion_texto ?? "")
      }else{
        setDireccionTexto("");
      }
     
      return;
    }

    setDireccionOk(intento.direccion_ok === true ? true : intento.direccion_ok === false ? false : null);
    setInternetOk(intento.internet_ok === true ? true : intento.internet_ok === false ? false : null);
    setDireccionTexto(intento.direccion_texto ?? "");
  }, [rbd, onModeChange]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!rbd) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        await reloadResumen();
      } catch (e) {
        console.error("Error cargando resumen intentos:", e);
        if (mounted) {
          setHasEstado(false);
          setEstadoStr(null);
          setDireccionOk(null);
          setInternetOk(null);
          setDireccionTexto("");
          onModeChange?.("none");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [rbd, reloadResumen, onModeChange]);

  useEffect(() => {
    if (direccion_ok === false) {
      setErrorDireccionTexto((direccion_texto ?? "").trim().length < 5);
    } else {
      setErrorDireccionTexto(false);
    }
  }, [direccion_ok, direccion_texto]);

  const canSubmit =
    !isPending &&
    (direccion_ok === true || direccion_ok === false) &&
    (internet_ok === true || internet_ok === false) &&
    (direccion_ok !== false || (direccion_texto ?? "").trim().length >= 5);

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving || loading || isPending) return;
    if (!canSubmit) return;

    try {
      setSaving(true);

      const payload = {
        direccion_ok: !!direccion_ok,
        internet_ok: !!internet_ok,
        direccion_texto: direccion_ok === false ? (direccion_texto ?? "") : "",
        creado_por: creadoPor ?? "",
        validar: false,
      };

      await axios.registrarDireccionRBD(rbd, payload);

      await reloadResumen();
      await Promise.resolve(onSaved?.());
    } catch (err) {
      console.error("Error guardando intento:", err);
    } finally {
      setSaving(false);
    }
  };

  const HeaderRow = ({ children, value, onChange, testYes, testNo }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "nowrap" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>

      <RadioGroup row value={value} onChange={onChange} sx={{ flexShrink: 0 }}>
        <FormControlLabel test_id={testYes} value="true" control={<Radio disabled={disableInputs} />} label="Si" />
        <FormControlLabel test_id={testNo} value="false" control={<Radio disabled={disableInputs} />} label="No" />
      </RadioGroup>
    </Box>
  );

  return (
    <Box sx={{ position: "relative" }}>
      {(loading || saving) ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.55)",
            borderRadius: "0.5rem",
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}

      <FormControl sx={{ width: "100%" }}>
        {isRejected ? (
          <Box
            sx={{
              background: "#D84315",
              color: "#FFF",
              borderRadius: "0.5rem",
              padding: "16px 20px",
              mb: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "inherit" }}>
              La dirección <b>‘{rejectedAddress}’ no fue validada por MINEDUC,</b> por favor regularice la situación y
              vuelva a ingresar la dirección actual correcta corrigiendo los campos a continuación. <br />
              Se mantendrá la dirección <b>‘{direccionActual}’</b> en la ficha de su establecimiento
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            m: margin,
            width: "100%",
            background: styles.boxBg,
            border: styles.border,
            borderRadius: "0.5rem",
            padding: "18px 22px",
            opacity: (loading || saving) ? 0.8 : 1,
          }}
        >
          <Grid container spacing={1.2}>
            <Grid size={12}>
              <HeaderRow
                value={direccion_ok === null ? "" : String(direccion_ok)}
                onChange={(e) => setDireccionOk(parseBool(e.target.value))}
                testYes={toSnakeCase("direccion_actual_si")}
                testNo={toSnakeCase("direccion_actual_no")}
              >
                <Typography variant="body1">¿Esta dirección es la correcta del establecimiento?</Typography>
              </HeaderRow>
            </Grid>

            <Grid size={12}>
              <Typography variant="body2">{direccionActual}</Typography>
            </Grid>

            {direccion_ok === false ? (
              <>
                <Grid size={12}>
                  <Divider />
                </Grid>

                <Grid size={12}>
                  <Typography variant="body2">
                    Si la dirección no es la correcta por favor dejar la corrección en el siguiente campo, MINEDUC
                    comprobará su cambio y actualizará el registro.
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <TextField
                    test_id={toSnakeCase("direccion_correccion")}
                    required
                    size="small"
                    value={direccion_texto}
                    onChange={(e) => setDireccionTexto(e.target.value)}
                    error={errorDireccionTexto && !isPending}
                    helperText={
                      !isPending && errorDireccionTexto ? "La dirección debe tener al menos 5 caracteres" : ""
                    }
                    sx={{ width: "100%" }}
                    disabled={disableInputs}
                  />
                </Grid>

                {isPending ? (
                  <Grid size={12}>
                    <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                      <InfoOutlinedIcon fontSize="small" sx={{ color: "#2196F3" }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        La revisión de la nueva dirección está en proceso.
                      </Typography>
                    </Box>
                  </Grid>
                ) : null}
              </>
            ) : null}
          </Grid>
        </Box>

        <Box
          sx={{
            m: margin,
            width: "100%",
            background: styles.boxBg,
            border: styles.border,
            borderRadius: "0.5rem",
            padding: "18px 22px",
            mt: 2,
            opacity: (loading || saving) ? 0.8 : 1,
          }}
        >
          <Grid container spacing={1.2}>
            <Grid size={12}>
              <HeaderRow
                value={internet_ok === null ? "" : String(internet_ok)}
                onChange={(e) => setInternetOk(parseBool(e.target.value))}
                testYes={toSnakeCase("internet_si")}
                testNo={toSnakeCase("internet_no")}
              >
                <Typography variant="body1">
                  <b>¿La dirección:</b>{" "}
                  <Typography component="span" variant="body1" sx={{ fontWeight: 400 }}>
                    {direccionParaInternet}
                  </Typography>{" "}
                  <b>tiene conexión a internet?</b>
                </Typography>
              </HeaderRow>
            </Grid>

            {isPending && internet_ok === false ? (
              <Grid size={12}>
                <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: "#2196F3" }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    El EES es responsable de gestionar el cambio de dirección con el operador para corregir el servicio
                    de conexión, ahora MINEDUC estará informado de esta situación.
                  </Typography>
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Box>

        {!isPending ? (
          <Box mt={2} display="flex" justifyContent="center">
            <PageButtonBlack
              width="20rem"
              disabled={loading || saving || !canSubmit}
              onClick={handleSave}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ borderRadius: 0 }}
              loading={saving}
            >
              Enviar respuesta
            </PageButtonBlack>
          </Box>
        ) : null}
      </FormControl>
    </Box>
  );
}
